// ─── reminders.js — Reminder system for CashHorizon ────────────────────────────
// Reminders can be attached to: transactions, recurring items, decisions, or standalone.
// Each reminder has a schedule and a brand scope (own brand or shared to others).
//
// Storage: LS.REMINDERS_KEY → array of Reminder objects
// Acknowledged: LS.REMINDERS_KEY + "_acked" → Set of reminder ids

import { LS } from "./storage.js";

// ─── Types ────────────────────────────────────────────────────────────────────
// Reminder = {
//   id: string,
//   title: string,
//   message: string,
//   type: "once"|"daily"|"weekly"|"monthly",
//   date: "YYYY-MM-DD" (ISO),
//   time: "HH:MM",
//   weekday: 0-6 (for weekly),
//   dayOfMonth: 1-31 (for monthly),
//   brandScope: "own" | "all" | string[] (brand keys),
//   ownBrand: string (brand key that created this),
//   attachedTo: { kind:"transaction"|"recurring"|"decision"|null, id:string } | null,
//   createdAt: number,
//   active: boolean,
// }

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function getReminders(brandKey) {
  const all = LS.getJson(LS.REMINDERS_KEY, []);
  return all.filter(r => r.active !== false && isRemindersForBrand(r, brandKey));
}

function isRemindersForBrand(r, brandKey) {
  if (r.ownBrand === brandKey) return true;
  if (r.brandScope === "all") return true;
  if (Array.isArray(r.brandScope) && r.brandScope.includes(brandKey)) return true;
  return false;
}

export function getAllReminders() {
  return LS.getJson(LS.REMINDERS_KEY, []);
}

export function saveReminder(reminder) {
  const all = LS.getJson(LS.REMINDERS_KEY, []);
  const idx = all.findIndex(r => r.id === reminder.id);
  if (idx >= 0) { all[idx] = reminder; } else { all.push(reminder); }
  LS.setJson(LS.REMINDERS_KEY, all);
}

export function deleteReminder(id) {
  const all = LS.getJson(LS.REMINDERS_KEY, []);
  LS.setJson(LS.REMINDERS_KEY, all.filter(r => r.id !== id));
}

export function acknowledgeReminder(id) {
  const acked = LS.getJson(LS.REMINDERS_KEY + "_acked", []);
  if (!acked.includes(id)) acked.push(id);
  LS.setJson(LS.REMINDERS_KEY + "_acked", acked);
}

export function isAcknowledged(id) {
  const acked = LS.getJson(LS.REMINDERS_KEY + "_acked", []);
  return acked.includes(id);
}

// ─── Due check ────────────────────────────────────────────────────────────────
export function getDueReminders(brandKey, now = new Date()) {
  const reminders = getReminders(brandKey);
  return reminders.filter(r => {
    if (isAcknowledged(r.id)) return false;
    return isReminderDue(r, now);
  });
}

function isReminderDue(r, now) {
  const today = now.toISOString().split("T")[0]; // YYYY-MM-DD
  const [hh, mm] = (r.time || "09:00").split(":").map(Number);
  const nowHH = now.getHours(), nowMM = now.getMinutes();
  const timeReached = nowHH > hh || (nowHH === hh && nowMM >= mm);

  if (r.type === "once") {
    return r.date === today && timeReached;
  }
  if (r.type === "daily") {
    const start = r.date || "2000-01-01";
    return today >= start && timeReached;
  }
  if (r.type === "weekly") {
    const start = r.date || "2000-01-01";
    const dow = r.weekday ?? now.getDay();
    return today >= start && now.getDay() === dow && timeReached;
  }
  if (r.type === "monthly") {
    const start = r.date ? start.split("-").map(Number) : [2000,1];
    const dom = r.dayOfMonth ?? 1;
    return now.getDate() === dom && timeReached;
  }
  return false;
}

// ─── Browser notification ──────────────────────────────────────────────────────
export async function requestNotifPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const perm = await Notification.requestPermission();
  return perm === "granted";
}

export function fireBrowserNotif(title, body) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    new Notification(title, { body, icon: "/favicon.ico" });
  } catch {}
}

// ─── Default reminder shape ───────────────────────────────────────────────────
export function newReminder(overrides = {}) {
  const today = new Date().toISOString().split("T")[0];
  return {
    id: "rem_" + Date.now(),
    title: "",
    message: "",
    type: "once",
    date: today,
    time: "09:00",
    weekday: 1,
    dayOfMonth: 1,
    brandScope: "own",
    ownBrand: LS.get(LS.BRAND, "main"),
    attachedTo: null,
    active: true,
    createdAt: Date.now(),
    ...overrides,
  };
}
